import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Search, Crosshair } from "lucide-react";
import { toast } from "sonner";

// Fix: react-leaflet/Webpack mangles default marker icon paths. Re-bind to CDN PNGs.
const DefaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function ClickHandler({ onClick }) {
  useMapEvents({ click(e) { onClick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

function PanTo({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) {
      map.flyTo([lat, lng], Math.max(map.getZoom(), 12), { duration: 0.6 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);
  return null;
}

/**
 * LocationPicker
 *  Props:
 *   lat, lng        — current pin (number or null)
 *   onChange(lat,lng) — fired on click, drag, search, or "use my location"
 *   radiusMiles     — if > 0, draws a circle around the pin (owner radius)
 *   height          — CSS height (default 280px)
 *   showRadius      — boolean
 *   showSearch      — show address search bar (default true)
 *   showLocateMe    — show "use my location" button (default true)
 */
export default function LocationPicker({
  lat,
  lng,
  onChange,
  radiusMiles = 0,
  height = "280px",
  showRadius = true,
  showSearch = true,
  showLocateMe = true,
  testid = "location-picker",
}) {
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const markerRef = useRef(null);

  // Geolocation is blocked inside iframes (e.g. the Emergent preview) unless the parent
  // explicitly passes allow="geolocation". Detect that and hide the button so users
  // aren't sent down a dead-end path. The button reappears when running standalone.
  // Native Capacitor apps run at https://localhost (not iframed) so the button shows.
  const inIframe = typeof window !== "undefined" && window.self !== window.top;
  const locateAvailable = showLocateMe && !inIframe;
  // Detect Capacitor native shell so we can use the Geolocation plugin which
  // properly requests Android runtime permissions (the bare `navigator.geolocation`
  // path in a Capacitor WebView silently fails without those permissions).
  const isNativeApp =
    typeof window !== "undefined" &&
    (!!window.Capacitor?.isNativePlatform?.() || window.location.protocol === "capacitor:");

  const center = useMemo(() => {
    if (lat != null && lng != null) return [lat, lng];
    return [37.7749, -122.4194]; // SF default
  }, [lat, lng]);

  const handleClick = (la, ln) => onChange(Number(la.toFixed(6)), Number(ln.toFixed(6)));

  const dragEnd = () => {
    const m = markerRef.current;
    if (!m) return;
    const p = m.getLatLng();
    onChange(Number(p.lat.toFixed(6)), Number(p.lng.toFixed(6)));
  };

  const search = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    try {
      // Nominatim — free OSM geocoder. Be respectful: 1 req/sec is the published limit.
      const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(query)}`;
      const res = await fetch(url, { headers: { "Accept-Language": navigator.language || "en" } });
      const data = await res.json();
      if (!data?.length) {
        toast.error("No matches — try a city, ZIP, or full address");
        return;
      }
      const r = data[0];
      onChange(Number(parseFloat(r.lat).toFixed(6)), Number(parseFloat(r.lon).toFixed(6)));
      toast.success(r.display_name?.split(",").slice(0, 2).join(", ") || "Location set");
    } catch (err) {
      toast.error("Search failed — check your connection");
    } finally {
      setSearching(false);
    }
  };

  const useMyLocation = async () => {
    setLocating(true);
    // Native path: use Capacitor Geolocation plugin so Android prompts for
    // permission and returns coordinates reliably.
    if (isNativeApp) {
      try {
        const { Geolocation } = await import("@capacitor/geolocation");
        const perm = await Geolocation.checkPermissions();
        if (perm.location !== "granted") {
          const req = await Geolocation.requestPermissions();
          if (req.location !== "granted") {
            toast.error("Location permission denied. Enable it in Settings → ShiftQuote → Permissions.");
            setLocating(false);
            return;
          }
        }
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
        onChange(Number(pos.coords.latitude.toFixed(6)), Number(pos.coords.longitude.toFixed(6)));
        toast.success("Location captured");
      } catch (err) {
        toast.error(err?.message || "Couldn't get location. Use the search box or click the map.");
      } finally {
        setLocating(false);
      }
      return;
    }
    // Web path: standard browser geolocation.
    if (!navigator.geolocation) {
      toast.error("Geolocation isn't supported here. Use the search box or click the map.");
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange(Number(pos.coords.latitude.toFixed(6)), Number(pos.coords.longitude.toFixed(6)));
        toast.success("Location captured");
        setLocating(false);
      },
      (err) => {
        if (err.code === 1) toast.error("Browser blocked location. Use the search box or click the map.");
        else if (err.code === 3) toast.error("Location request timed out. Use the search box or click the map.");
        else toast.error(err.message || "Use the search box or click the map instead.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // 1 mile ≈ 1609.34 meters
  const radiusMeters = radiusMiles && radiusMiles > 0 ? radiusMiles * 1609.34 : 0;

  return (
    <div className="space-y-2" data-testid={testid}>
      {(showSearch || locateAvailable) && (
        <div className="flex items-center gap-2">
          {showSearch && (
            <form onSubmit={search} className="flex-1 relative">
              <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search address, city, or ZIP..."
                className="input-base pl-10 pr-20 text-sm"
                data-testid="map-search-input"
              />
              <button
                type="submit"
                disabled={searching}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white px-2 py-1"
                data-testid="map-search-btn"
              >
                {searching ? "..." : "Find"}
              </button>
            </form>
          )}
          {locateAvailable && (
            <button
              type="button"
              onClick={useMyLocation}
              disabled={locating}
              className="btn-ghost text-[10px] uppercase tracking-widest flex items-center gap-1 whitespace-nowrap"
              data-testid="map-locate-btn"
              title="Use my location"
            >
              <Crosshair className="w-3 h-3" /> {locating ? "..." : "Locate"}
            </button>
          )}
        </div>
      )}

      <div className="border border-white/10 overflow-hidden" style={{ height }}>
        <MapContainer center={center} zoom={lat != null ? 12 : 5} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ClickHandler onClick={handleClick} />
          <PanTo lat={lat} lng={lng} />
          {lat != null && lng != null && (
            <>
              <Marker
                position={[lat, lng]}
                draggable
                eventHandlers={{ dragend: dragEnd }}
                ref={markerRef}
              />
              {showRadius && radiusMeters > 0 && (
                <Circle
                  center={[lat, lng]}
                  radius={radiusMeters}
                  pathOptions={{ color: "#FF3B30", fillColor: "#FF3B30", fillOpacity: 0.12, weight: 2 }}
                />
              )}
            </>
          )}
        </MapContainer>
      </div>

      <div className="flex items-center justify-between text-[11px] text-zinc-500 font-mono">
        {lat != null && lng != null
          ? <span data-testid="map-coords">{lat.toFixed(4)}, {lng.toFixed(4)}{radiusMiles ? `  ·  ${radiusMiles} mi radius` : ""}</span>
          : <span className="italic">{locateAvailable ? "Click the map, search an address, or use the locate button." : "Click the map or search an address."}</span>}
        <span className="text-zinc-700">© OpenStreetMap</span>
      </div>
    </div>
  );
}
